import { Button } from "@light-technology/components";
import { Table } from "@untitledui/icons";
import { format } from "date-fns";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../../state/api";
import { ALL_STATUSES, ExportStatus } from "../../state/api.types";
import { downloadFile } from "../../utilities/fileDownload";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";

type ExportAccountsButtonProps = {
  appUUID: string;
};

const DOWNLOAD_ERROR =
  "The CSV could not be downloaded at this time. Please try again later.";

const TERMINAL_STATUSES: ExportStatus[] = [
  ExportStatus.COMPLETED,
  ExportStatus.FAILED,
  ExportStatus.CANCELLED,
];

export const ExportAccountsButton = (props: ExportAccountsButtonProps) => {
  const hasDownloadedRef = useRef(false);

  const [exportAccounts, { data: exportResult, isLoading: isStarting }] =
    useMutationWithToast(api.useExportAccountsMutation(), {
      errorMessage: DOWNLOAD_ERROR,
    });

  // The mutation result is the source of truth for the export being polled.
  const exportUUID = exportResult?.export_uuid;
  const exportArgs = { exportUUID: exportUUID ?? "", appUUID: props.appUUID };

  // Read the cached status separately from the subscription below, so the
  // polling interval can be derived from the status it produces. `currentData`
  // rather than `data`, which holds the previous export's result until the new
  // one responds — long enough to read as already settled.
  const { currentData: exportData, error: exportError } =
    api.endpoints.getExport.useQueryState(exportArgs, { skip: !exportUUID });

  const isSettled =
    !!exportError ||
    (!!exportData && TERMINAL_STATUSES.includes(exportData.status));

  // Poll every second, and stop once the export settles.
  api.endpoints.getExport.useQuerySubscription(exportArgs, {
    skip: !exportUUID,
    pollingInterval: isSettled ? 0 : 1000,
  });

  // Note: Always export ALL accounts regardless of UI filters.
  const handleExport = () => {
    hasDownloadedRef.current = false;
    // Errors are surfaced by useMutationWithToast.
    void exportAccounts({
      appUUID: props.appUUID,
      filter: ALL_STATUSES,
      search: "",
    });
  };

  // Hand the finished export off to the browser.
  useEffect(() => {
    if (exportError) {
      toast.error(DOWNLOAD_ERROR);
      return;
    }

    if (
      exportData?.status === ExportStatus.COMPLETED &&
      exportData.download_url
    ) {
      // Guard against a second poll landing before polling winds down.
      if (hasDownloadedRef.current) return;
      hasDownloadedRef.current = true;

      // Download straight from S3 rather than fetching it into memory, which
      // avoids CORS issues and keeps the whole file off the heap.
      downloadFile(
        exportData.download_url,
        exportData.file_name ??
          `accounts-${format(new Date(), "yyyyMMdd-HHmmss")}.csv`,
      );
      toast.success("Accounts CSV has been downloaded");
    } else if (
      exportData?.status === ExportStatus.FAILED ||
      exportData?.status === ExportStatus.CANCELLED
    ) {
      toast.error(DOWNLOAD_ERROR);
    }
  }, [exportData, exportError]);

  return (
    <Button
      size="md"
      onClick={handleExport}
      isLoading={isStarting || (!!exportUUID && !isSettled)}
      iconLeading={Table}
      data-ph-capture-attribute-action="export-accounts-csv"
    >
      Download CSV
    </Button>
  );
};
